import { useState, useEffect, useCallback } from 'react'
import {
  Typography,
  Paper,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Divider
} from '@mui/material'
import ExportButton from '../../components/ExportButton'
import ProgressBar from '../../components/ProgressBar'
import FieldSelector, { FieldConfiguration, SelectedField } from '../../components/FieldSelector'
import ExportFilters from '../../components/ExportFilters'
import { lightspeedApi } from '../../services/api'
import { useFranchise } from '../../contexts/FranchiseContext'
import { useExportState } from '../../hooks/useExportState'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useAsyncExport } from '../../hooks/useAsyncExport'
import { EXPORT_TYPES, defaultSalesExportState, type SalesExportState } from '../../types/exportStates'

function LightspeedSales() {
  usePageTitle('Lightspeed', 'Sales Export')
  const { fpCode } = useFranchise()
  const { state: exportState, updateState: updateExportState } = useExportState<SalesExportState>({
    exportType: EXPORT_TYPES.LIGHTSPEED_SALES,
    defaultState: defaultSalesExportState
  })
  
  const { dateFrom, dateTo, includeLineItems } = exportState

  // Use async export hook
  const {
    loading,
    error: exportError,
    success: exportSuccess,
    warning: exportWarning,
    progressMessage,
    currentCount,
    startExport
  } = useAsyncExport({
    exportType: 'sales'
  })

  const [fieldConfiguration, setFieldConfiguration] = useState<FieldConfiguration | null>(null)
  const [selectedFields, setSelectedFields] = useState<SelectedField[]>([])
  const [localError, setLocalError] = useState<string | null>(null)
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('csv')

  // Combine errors from hook and local validation
  const error = exportError || localError
  const warning = exportWarning

  // Load field configuration on mount
  useEffect(() => {
    const loadFieldConfiguration = async () => {
      try {
        const response = await lightspeedApi.getSalesFields()
        setFieldConfiguration(response.data)
      } catch (error) {
        console.error('Failed to load field configuration:', error)
        setLocalError('Failed to load export field configuration')
      }
    }

    loadFieldConfiguration()
  }, [])

  const handleFieldsChange = useCallback((fields: SelectedField[]) => {
    setSelectedFields(fields)
  }, [])

  const handleExport = async () => {
    if (!fpCode) {
      setLocalError('Please select a franchise partner')
      return
    }

    setLocalError(null)

    // Validate date range
    if (dateFrom && dateTo) {
      if (dateFrom > dateTo) {
        setLocalError(`Invalid date range: 'From' date (${dateFrom}) is after 'To' date (${dateTo}). Please swap the dates.`)
        return
      }
    }

    // Build export parameters
    const fieldIds = selectedFields.map(f => f.id).join(',')

    const params: Record<string, any> = {
      fp_code: fpCode,
      format: exportFormat,
      include_line_items: includeLineItems
    }

    // Only add date parameters if they have values
    if (dateFrom && dateFrom.trim()) {
      params.date_from = dateFrom.trim()
    }
    if (dateTo && dateTo.trim()) {
      params.date_to = dateTo.trim()
    }

    // Only add selected_fields if user has made a selection
    if (fieldIds) {
      params.selected_fields = fieldIds
    }

    // Start the async export
    await startExport(params)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!fpCode && (
            <Alert severity="info">
              Please select a franchise partner from the dropdown above to continue.
            </Alert>
          )}


          {fpCode && (
            <>
              <ExportFilters
                title="Filters"
                filters={[
                  {
                    id: 'daterange',
                    label: 'Date Range',
                    type: 'daterange',
                    value: { from: dateFrom, to: dateTo },
                    onChange: (value) => {
                      updateExportState({ 
                        dateFrom: value.from,
                        dateTo: value.to
                      })
                    }
                  }
                ]}
              />

              <Paper sx={{ p: 3 }}>
                <FieldSelector
                  configuration={fieldConfiguration}
                  onFieldsChange={handleFieldsChange}
                  exportType="lightspeed_sales"
                />
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  ⚙️ Export Options
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <RadioGroup
                    row
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value as 'csv' | 'xlsx')}
                  >
                    <FormControlLabel 
                      value="csv" 
                      control={<Radio sx={{ color: 'info.main', '&.Mui-checked': { color: 'info.main' } }} />} 
                      label="CSV File (Recommended)" 
                    />
                    <FormControlLabel 
                      value="xlsx" 
                      control={<Radio sx={{ color: 'info.main', '&.Mui-checked': { color: 'info.main' } }} />} 
                      label="XLSX File (Not ideal for large datasets)" 
                    />
                  </RadioGroup>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeLineItems}
                        onChange={(e) => updateExportState({ includeLineItems: e.target.checked })}
                        sx={{ color: 'info.main', '&.Mui-checked': { color: 'info.main' } }}
                      />
                    }
                    label="Include line items (detailed product breakdown)"
                  />
                </Box>
              </Paper>

          {error && (
            <Alert severity="error" onClose={() => setLocalError(null)}>
              {error}
            </Alert>
          )}

          {warning && (
            <Alert severity="warning">
              {warning}
            </Alert>
          )}

          {exportSuccess && (
            <Alert severity="success">
              {exportSuccess}
            </Alert>
          )}

          {loading && (
            <Box>
              <ProgressBar
                indeterminate
                message={progressMessage || "Exporting sales... This may take a few minutes depending on the date range."}
              />
              {currentCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Records processed: {currentCount.toLocaleString()}
                </Typography>
              )}
            </Box>
          )}

              <ExportButton
                onClick={handleExport}
                loading={loading}
                disabled={!fpCode || selectedFields.length === 0}
                text="Export Sales"
              />

              {selectedFields.length === 0 && (
                <Alert severity="warning">
                  Please select at least one field to export.
                </Alert>
              )}


            </>
          )}
      </Box>
    </Box>
  )
}

export default LightspeedSales